import { useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import BarChartIcon from '@mui/icons-material/BarChart';
import CategoryIcon from '@mui/icons-material/Category';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { useAuth } from '../../contexts/AuthContext';
import { MetricCard } from './components/MetricCard';
import { UsageChart } from './components/UsageChart';
import { ScoreDistribution } from './components/ScoreDistribution';
import { CategoryRadar } from './components/CategoryRadar';
import { UserLeaderboard } from './components/UserLeaderboard';
import { AnalyticsSkeleton } from './components/AnalyticsSkeleton';
import { PageHeader } from '../../components/common/PageHeader';
import { SectionCard } from '../../components/common/SectionCard';
import { EmptyState } from '../../components/common/EmptyState';
import { MyScoresTable } from './components/MyScoresTable';
import { OrgSummaryCards } from './components/OrgSummaryCards';
import { OrgUsersTable } from './components/OrgUsersTable';
import {
  useGetSummary,
  useGetScores,
  useGetUserMetrics,
  useGetMyScores,
  useGetMyTrends,
  useGetOrgSummary,
  useGetOrgUsers,
} from '../../api/generated/generated';
import type {
  DashboardSummary,
  ScoreAnalytics,
  UserMetricsResponse,
  MyScoresResponse,
  MyTrendsResponse,
  OrgSummaryResponse,
  OrgUsersResponse,
} from '../../api/generated';

type Period = 'today' | 'this_week' | 'this_month';

const PERIOD_LABELS: Record<Period, string> = {
  today: 'Today',
  this_week: 'This Week',
  this_month: 'This Month',
};

export function AnalyticsPage() {
  const { user } = useAuth();
  const isManager =
    user?.roles?.includes('sales_manager') || user?.roles?.includes('org_owner') || user?.superAdmin;

  const [tab, setTab] = useState(0);
  const [period, setPeriod] = useState<Period>('this_month');

  // Existing metrics hooks (Dashboard tab)
  const { data: summaryRes, isLoading: summaryLoading } = useGetSummary({ period });
  const summary = summaryRes?.data as DashboardSummary | undefined;
  const { data: scoresRes } = useGetScores();
  const scores = scoresRes?.data as ScoreAnalytics | undefined;
  const { data: userMetricsRes } = useGetUserMetrics();
  const userMetrics = userMetricsRes?.data as UserMetricsResponse | undefined;

  // My Scores hooks
  const { data: myScoresRes } = useGetMyScores();
  const myScores = myScoresRes?.data as MyScoresResponse | undefined;
  const { data: myTrendsRes } = useGetMyTrends();
  const myTrends = myTrendsRes?.data as MyTrendsResponse | undefined;

  // Org hooks (only for managers)
  const { data: orgSummaryRes } = useGetOrgSummary(undefined, { query: { enabled: !!isManager } });
  const orgSummary = orgSummaryRes?.data as OrgSummaryResponse | undefined;
  const { data: orgUsersRes } = useGetOrgUsers(undefined, { query: { enabled: !!isManager } });
  const orgUsers = orgUsersRes?.data as OrgUsersResponse | undefined;

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, value: Period | null) => {
    if (value) setPeriod(value);
  };

  if (summaryLoading) {
    return <AnalyticsSkeleton />;
  }

  const conversations = summary?.conversations;
  const scoreMetrics = summary?.scores;
  const llm = summary?.llm;

  return (
    <Box>
      <PageHeader
        title="Analytics"
        actions={
          tab === 0 ? (
            <ToggleButtonGroup value={period} exclusive onChange={handlePeriodChange} size="small">
              {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
                <ToggleButton key={p} value={p}>
                  {PERIOD_LABELS[p]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          ) : undefined
        }
      />
      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Dashboard" />
        <Tab label="My Scores" />
        {isManager && <Tab label="Organization" />}
      </Tabs>
      {/* Dashboard Tab */}
      {tab === 0 && (
        <>
          {/* Metric Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Pre-call Research"
                value={conversations?.pre_call_count ?? 0}
                change={conversations?.change_percent}
                icon={<SearchIcon />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Call Analyses"
                value={conversations?.post_call_count ?? 0}
                change={conversations?.change_percent}
                icon={<AssessmentIcon />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Average Score"
                value={scoreMetrics?.avg_overall ?? null}
                change={scoreMetrics?.change_percent}
                icon={<TrendingUpIcon />}
                format="score"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Messages"
                value={conversations?.total_messages ?? 0}
                icon={<ChatBubbleIcon />}
              />
            </Grid>
          </Grid>

          {/* Charts Row */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <SectionCard title="Score Trends">
                {scores?.trend && scores.trend.length > 0 ? (
                  <UsageChart data={scores.trend} />
                ) : (
                  <EmptyState
                    icon={<TrendingUpIcon />}
                    title="No score data yet"
                    description="Score trends will appear after call analyses."
                  />
                )}
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SectionCard title="Score by Category" sx={{ height: '100%' }}>
                {scoreMetrics?.avg_by_category &&
                Object.keys(scoreMetrics.avg_by_category).length > 0 ? (
                  <CategoryRadar data={scoreMetrics.avg_by_category} />
                ) : (
                  <EmptyState
                    icon={<CategoryIcon />}
                    title="No category data yet"
                  />
                )}
              </SectionCard>
            </Grid>
          </Grid>

          {/* Score Distribution + LLM Usage */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard title="Score Distribution">
                {scoreMetrics?.score_distribution ? (
                  <ScoreDistribution data={scoreMetrics.score_distribution} />
                ) : (
                  <EmptyState
                    icon={<BarChartIcon />}
                    title="No score data yet"
                  />
                )}
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard title="LLM Usage" sx={{ height: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{
                      color: "text.secondary"
                    }}>Requests</Typography>
                    <Typography sx={{
                      fontWeight: 600
                    }}>
                      {(llm?.total_requests ?? 0).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{
                      color: "text.secondary"
                    }}>Total Tokens</Typography>
                    <Typography sx={{
                      fontWeight: 600
                    }}>
                      {(llm?.total_tokens ?? 0).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{
                      color: "text.secondary"
                    }}>Avg Response Time</Typography>
                    <Typography sx={{
                      fontWeight: 600
                    }}>
                      {llm?.avg_response_time_ms !== null &&
                      llm?.avg_response_time_ms !== undefined
                        ? `${(llm.avg_response_time_ms / 1000).toFixed(1)}s`
                        : '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{
                      color: "text.secondary"
                    }}>Errors</Typography>
                    <Typography sx={{
                      fontWeight: 600
                    }}>
                      {(llm?.error_count ?? 0).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </SectionCard>
            </Grid>
          </Grid>

          {/* User Performance */}
          {userMetrics?.users && userMetrics.users.length > 0 && (
            <SectionCard title="User Performance">
              <UserLeaderboard users={userMetrics.users} />
            </SectionCard>
          )}
        </>
      )}
      {/* My Scores Tab */}
      {tab === 1 && (
        <>
          {/* Trend Summary */}
          {myTrends && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h3" color="primary" sx={{
                    fontWeight: 700
                  }}>
                    {myTrends.overall_avg ?? '—'}
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    Average Score
                  </Typography>
                </Paper>
              </Grid>
              {myTrends.category_trends.map((ct) => (
                <Grid key={ct.category} size={{ xs: 6, sm: 2 }}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{
                      fontWeight: 600
                    }}>
                      {ct.current_avg ?? '—'}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        textTransform: 'capitalize'
                      }}>
                      {ct.category}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Scores Table */}
          <SectionCard title="Recent Scored Calls">
            <MyScoresTable calls={myScores?.calls ?? []} />
          </SectionCard>
        </>
      )}
      {/* Organization Tab (managers only) */}
      {tab === 2 && isManager && (
        <>
          {orgSummary && <OrgSummaryCards summary={orgSummary} />}

          {orgUsers && orgUsers.users.length > 0 && (
            <SectionCard title="Team Members" sx={{ mt: 3 }}>
              <OrgUsersTable users={orgUsers.users} />
            </SectionCard>
          )}
        </>
      )}
    </Box>
  );
}
